import { Button, Container, Label } from '@playcanvas/pcui';

import { MAX_CONSOLE_ENTRIES, type ConsoleEntry } from '../console-log';
import { Events } from '../events';
import { localize } from './localization';

const pad = (value: number) => String(value).padStart(2, '0');

const formatTime = (time: number) => {
    const date = new Date(time);
    return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

// The Console dock tab: warnings and errors recorded by console-log.ts, oldest first.
class ConsolePanel extends Container {
    private editorEvents: Events;

    private list: Container;

    private empty: Label;

    private summary: Label;

    private badge: HTMLElement | null = null;

    private errors = 0;

    private warnings = 0;

    private unseen = 0;

    private unseenErrors = false;

    constructor(events: Events, args = {}) {
        args = {
            ...args,
            id: 'console-panel',
            hidden: true
        };

        super(args);

        this.editorEvents = events;

        const toolbar = new Container({
            id: 'console-toolbar'
        });

        this.summary = new Label({
            id: 'console-summary'
        });

        const clear = new Button({
            id: 'console-clear',
            text: localize('console.clear')
        });
        clear.on('click', () => events.fire('console.clear'));

        toolbar.append(this.summary);
        toolbar.append(clear);

        this.list = new Container({
            id: 'console-list'
        });
        this.list.dom.setAttribute('role', 'log');

        this.empty = new Label({
            id: 'console-empty',
            text: localize('console.empty')
        });
        this.list.append(this.empty);

        this.append(toolbar);
        this.append(this.list);

        (events.invoke('console.entries') as ConsoleEntry[]).forEach(entry => this.addRow(entry));
        this.updateSummary();

        events.on('console.entry', (entry: ConsoleEntry) => {
            this.addRow(entry);
            this.updateSummary();
        });

        events.on('console.cleared', () => {
            this.list.dom.querySelectorAll('.console-row').forEach(row => row.remove());
            this.errors = 0;
            this.warnings = 0;
            this.setUnseen(0, false);
            this.updateSummary();
        });

        events.on('bottomDock.panelChanged', (panel: string | null) => {
            if (panel === 'console') {
                this.setUnseen(0, false);
                this.list.dom.scrollTop = this.list.dom.scrollHeight;
            }
        });
    }

    // Shows how many warnings and errors arrived while the tab was closed.
    bindTab(tab: Button) {
        this.badge = document.createElement('span');
        this.badge.className = 'bottom-tab-badge';
        tab.dom.appendChild(this.badge);
        this.setUnseen(this.unseen, this.unseenErrors);
    }

    private addRow(entry: ConsoleEntry) {
        const list = this.list.dom;
        const atBottom = list.scrollHeight - list.scrollTop - list.clientHeight < 4;

        const row = document.createElement('div');
        row.className = `console-row console-${entry.level}`;

        const time = document.createElement('span');
        time.className = 'console-time';
        time.textContent = formatTime(entry.time);

        const level = document.createElement('span');
        level.className = 'console-level';
        level.textContent = localize(`console.level.${entry.level}`);

        // textContent, never innerHTML: messages can carry file names and server responses
        const message = document.createElement('span');
        message.className = 'console-message';
        message.textContent = entry.message;

        row.append(time, level, message);
        list.appendChild(row);

        if (entry.level === 'error') {
            this.errors++;
        } else {
            this.warnings++;
        }

        // mirror the log's cap so the list never holds more rows than the log keeps
        const rows = list.querySelectorAll('.console-row');
        if (rows.length > MAX_CONSOLE_ENTRIES) {
            const oldest = rows[0];
            if (oldest.classList.contains('console-error')) {
                this.errors--;
            } else {
                this.warnings--;
            }
            oldest.remove();
        }

        if (atBottom) {
            list.scrollTop = list.scrollHeight;
        }

        if (this.editorEvents.invoke('bottomDock.panel') !== 'console') {
            this.setUnseen(this.unseen + 1, this.unseenErrors || entry.level === 'error');
        }
    }

    private setUnseen(count: number, hasErrors: boolean) {
        this.unseen = count;
        this.unseenErrors = hasErrors;

        if (this.badge) {
            this.badge.textContent = count > 99 ? '99+' : String(count);
            this.badge.hidden = count === 0;
            this.badge.classList[hasErrors ? 'add' : 'remove']('has-errors');
        }
    }

    private updateSummary() {
        this.summary.text = `${localize('console.errors')} ${this.errors} · ${localize('console.warnings')} ${this.warnings}`;
        this.empty.hidden = this.errors + this.warnings > 0;
    }
}

export { ConsolePanel };
